import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FeatureRequest, RequestStatus, fetchFeatureRequests, postFeatureRequest,
  setFeatureRequestStatus, voteFeatureRequest,
} from '../../api/site';
import { useSiteViewer } from './SiteLayout';
import { timeAgo } from './ForumPage';

const STATUS_LABEL: Record<RequestStatus, string> = {
  open: 'Open',
  planned: 'Planned',
  in_progress: 'In progress',
  shipped: 'Shipped',
  declined: 'Declined',
};
const STATUSES = Object.keys(STATUS_LABEL) as RequestStatus[];
/** The status pill's colour pair (ABC6-07: Planned blue, Shipped green). */
const STATUS_TONE: Record<RequestStatus, string> = {
  open: 'neutral', planned: 'info', in_progress: 'warn', shipped: 'ok', declined: 'danger',
};

/**
 * The feature-request board: ranked by score, one up/down vote per signed-in
 * user (click again to withdraw), statuses set by staff. R13 (ABC6-07): each
 * request is a card with its vote box on the left.
 */
export default function FeatureRequestsPage() {
  const viewer = useSiteViewer();
  const [requests, setRequests] = useState<FeatureRequest[] | null>(null);
  const [canVote, setCanVote] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [error, setError] = useState('');

  const [composing, setComposing] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [postError, setPostError] = useState('');

  const load = () =>
    fetchFeatureRequests()
      .then((r) => { setRequests(r.requests); setCanVote(r.canVote); setIsStaff(r.isStaff); })
      .catch((e) => setError(e.message || 'Could not load feature requests'));

  useEffect(() => { load(); }, []);

  /** Clicking your current vote withdraws it; anything else casts/switches. */
  async function vote(r: FeatureRequest, dir: -1 | 1) {
    const next = r.my_vote === dir ? 0 : dir;
    try {
      await voteFeatureRequest(r.id, next as -1 | 0 | 1);
      await load();
    } catch (err: any) {
      setError(err.message || 'Vote failed');
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setPostError(''); setBusy(true);
    try {
      await postFeatureRequest({ title: title.trim(), body: body.trim() });
      setTitle(''); setBody(''); setComposing(false);
      await load();
    } catch (err: any) {
      setPostError(err.message || 'Could not post the request');
    } finally {
      setBusy(false);
    }
  }

  async function changeStatus(r: FeatureRequest, status: RequestStatus) {
    try {
      await setFeatureRequestStatus(r.id, status);
      await load();
    } catch (err: any) {
      setError(err.message || 'Status change failed');
    }
  }

  return (
    <div>
      <div className="comm-toolbar">
        <p className="comm-intro">
          Vote for what Form-Magic should build next. Ranked by votes; statuses are set by the team.
        </p>
        {viewer && (
          <button type="button" className={`fs-btn ${composing ? 'neutral' : 'ink'}`} onClick={() => setComposing((c) => !c)}>
            {composing ? 'Cancel' : 'New request'}
          </button>
        )}
      </div>

      {error && <div className="comm-err">{error}</div>}

      {viewer === null && (
        <div className="comm-signin">
          <Link to="/login">Sign in</Link> or <Link to="/pricing">create an account</Link> to vote
          and post requests. The board is readable by everyone.
        </div>
      )}

      {composing && viewer && (
        <form className="comm-form" onSubmit={submit}>
          <h3>Request a feature</h3>
          {postError && <div className="comm-err">{postError}</div>}
          <div className="fs-field">
            <label htmlFor="request-title">Title</label>
            <input id="request-title" type="text" value={title} maxLength={140} onChange={(e) => setTitle(e.target.value)}
              placeholder="“Webhook delivery target”" />
          </div>
          <div className="fs-field">
            <label htmlFor="request-body">Why it matters</label>
            <textarea id="request-body" value={body} rows={5} onChange={(e) => setBody(e.target.value)}
              placeholder="The problem you are solving — the use case persuades voters better than the solution." />
          </div>
          <div>
            <button className="fs-btn primary" disabled={busy || title.trim().length < 4 || !body.trim()}>
              {busy ? 'Posting…' : 'Post request'}
            </button>
          </div>
        </form>
      )}

      {requests && requests.length === 0 && <div className="site-empty">No requests yet — post the first one.</div>}
      {requests && requests.length > 0 && (
        <div className="req-list">
          {requests.map((r) => (
            <div key={r.id} className="req-row">
              <div className="req-votes">
                <button
                  type="button"
                  className={`up${r.my_vote === 1 ? ' on' : ''}`}
                  title={canVote ? (r.my_vote === 1 ? 'Withdraw your upvote' : 'Vote up') : 'Sign in to vote'}
                  aria-label={canVote ? (r.my_vote === 1 ? 'Withdraw your upvote' : 'Vote up') : 'Sign in to vote'}
                  aria-pressed={r.my_vote === 1}
                  disabled={!canVote}
                  onClick={() => vote(r, 1)}
                >▲</button>
                <div className="score" title={`${r.up_votes} up · ${r.down_votes} down`}>{r.score}</div>
                <button
                  type="button"
                  className={`down${r.my_vote === -1 ? ' on' : ''}`}
                  title={canVote ? (r.my_vote === -1 ? 'Withdraw your downvote' : 'Vote down') : 'Sign in to vote'}
                  aria-label={canVote ? (r.my_vote === -1 ? 'Withdraw your downvote' : 'Vote down') : 'Sign in to vote'}
                  aria-pressed={r.my_vote === -1}
                  disabled={!canVote}
                  onClick={() => vote(r, -1)}
                >▼</button>
              </div>
              <div className="req-main">
                <h3>
                  {r.title}
                  <span className={`fs-pill status-pill ${STATUS_TONE[r.status]} ${r.status}`}>{STATUS_LABEL[r.status]}</span>
                </h3>
                <div className="body">{r.body}</div>
                {r.staff_note && <div className="staff-note">Form-Magic team: {r.staff_note}</div>}
                <div className="meta">
                  {r.author_name}{r.author_is_staff && <> <span className="fs-pill identity">Form-Magic team</span></>} · {timeAgo(r.created_at)}
                  {' · '}{r.up_votes}▲ {r.down_votes}▼
                  {isStaff && (
                    <>
                      {' · '}
                      <select
                        className="req-status-select"
                        aria-label={`Status of “${r.title}”`}
                        value={r.status}
                        onChange={(e) => changeStatus(r, e.target.value as RequestStatus)}
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                      </select>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
